import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Field, Form } from "formik";
import moment from "moment";
import classNames from "classnames";

import AmountField from "pages/_components/fields/formik/AmountField";
import { DateField } from "pages/_components/fields/DateField";
import Selector from "pages/_components/fields/formik/Selector";
import TextField from "pages/_components/fields/TextField";
import AlfaNumericStgField from "pages/_components/fields/AlfaNumericStgField";
import Button from "pages/_components/Button";
import Container from "pages/_components/Container";
import Dropdown from "pages/_components/Dropdown";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";

import * as configUtils from "util/config";
import * as i18nUtils from "util/i18n";

import { actions as holidaysActions, selectors as holidaysSelector } from "reducers/holidays";
import RangeDatePicker from "pages/_components/fields/rangedatepicker/RangeDatePicker";
import { evalEqualsValues } from "util/form";

const FORM_ID = "accounts.movements";

function MovementsFilters({
    dispatch,
    isDesktop,
    values,
    selectedAccount,
    isFetchingMovements,
    latestMovementsPageNumber,
    pendingMovementsPageNumber,
    findBy,
    isLatestMovementsSelected,
    lastDateFrom,
    lastDateTo,
    lastDates,
    pendingDateFrom,
    pendingDateTo,
    pendingDates,
    amountFrom,
    amountTo,
    voucher,
    detail,
    longDetail,
    movementType,
    getPendingMinDate,
    handleMovementToShow,
    setDates,
    holidays,
    downloadExportValues,
    fetchingDownload,
    setDownloadValues,
    filters,
}) {
    const TYPE_OPTIONS = [
        {
            value: 1,
            label: i18nUtils.get("accounts.movements.findBy.type.label.credit").toUpperCase(),
        },
        {
            value: 0,
            label: i18nUtils.get("accounts.movements.findBy.type.label.debit").toUpperCase(),
        },
        {
            value: 2,
            label: i18nUtils.get("accounts.movements.findBy.type.label.all").toUpperCase(),
        },
    ];

    const BASE_OPTIONS = [
        {
            value: "period",
            label: i18nUtils.get("accounts.movements.findBy.period.label").toUpperCase(),
        },
        {
            value: "amount",
            label: i18nUtils.get("accounts.movements.findBy.amount.label").toUpperCase(),
        },
        {
            value: "detail",
            label: i18nUtils.get("accounts.movements.findBy.detail.label").toUpperCase(),
        },
    ];

    const [showForm, setShowForm] = useState(false);

    const enableRangeDatefield = configUtils.getBoolean("frontend.show.RangeDatePicker.functionalities", false);
    const enableNewSubmitButton = configUtils.getBoolean("frontend.Show.FilterButton.Functionalities", false);

    useEffect(() => {
        dispatch(holidaysActions.listHolidays());
    }, [dispatch]);

    const currencies = [{ id: 0, label: i18nUtils.get(`currency.label.${selectedAccount.currency}`) }];

    const getMaxDateFrom = () => {
        const maxDateFrom = configUtils.getInteger("accounts.max.dateFrom", 3);

        return moment()
            .startOf("day")
            .add(maxDateFrom * -1, "months");
    };

    const getLatestMovementsOptions = () => {
        const options = BASE_OPTIONS;

        options.push({
            value: "voucher",
            label: i18nUtils.get("accounts.movements.findBy.voucher.label").toUpperCase(),
        });

        options.push({
            value: "longDetail",
            label: i18nUtils.get("accounts.movements.findBy.longDetail.label").toUpperCase(),
        });

        return options;
    };

    const getPendingMovementsOptions = () => {
        const options = BASE_OPTIONS;

        options.push({
            value: "movementType",
            label: i18nUtils.get("accounts.movements.findBy.operations.label").toUpperCase(),
        });

        return options;
    };

    const isValidAmount = (amount) => amount && amount.amount.toString().length > 0;

    const isButtonDisabled =
        (findBy === "period" &&
            !enableRangeDatefield &&
            isLatestMovementsSelected &&
            ((!lastDateFrom && !lastDateTo) || (lastDateFrom && lastDateTo && lastDateFrom > lastDateTo))) ||
        (findBy === "period" &&
            enableRangeDatefield &&
            isLatestMovementsSelected &&
            !!lastDates?.length &&
            !(lastDates[0] && lastDates[1])) ||
        (findBy === "period" &&
            !enableRangeDatefield &&
            !isLatestMovementsSelected &&
            ((!pendingDateFrom && !pendingDateTo) ||
                (pendingDateFrom && pendingDateTo && pendingDateFrom > pendingDateTo))) ||
        (findBy === "period" &&
            enableRangeDatefield &&
            !isLatestMovementsSelected &&
            pendingDates?.length &&
            !(pendingDates[0] && pendingDates[1])) ||
        (findBy === "amount" &&
            ((!isValidAmount(amountFrom) && !isValidAmount(amountTo)) ||
                (isValidAmount(amountFrom) &&
                    isValidAmount(amountTo) &&
                    Number(amountFrom.amount) > Number(amountTo.amount)))) ||
        (findBy === "voucher" && !voucher) ||
        (findBy === "detail" && !detail) ||
        (findBy === "longDetail" && !longDetail) ||
        (findBy === "movementType" && typeof movementType !== "number" && !movementType);
    const maxDays = configUtils.getInteger("movements.maxDays.default", enableRangeDatefield ? 0 : 10);

    const handleChangeDate = (fieldName) => (selectedDate, form) => {
        let dateTo;
        let dateFrom;
        let diff;

        if (fieldName === "pendingDateFrom") {
            diff = pendingDateTo ? Math.abs(moment(pendingDateTo)?.diff(selectedDate, "days")) : null;
            dateFrom = selectedDate;
            dateTo = diff && diff > maxDays ? moment(selectedDate)?.add(maxDays, "days") : pendingDateTo;
        } else {
            diff = pendingDateFrom ? moment(selectedDate)?.diff(pendingDateFrom, "days") : null;
            dateTo = selectedDate;
            dateFrom = diff && diff > maxDays ? moment(selectedDate)?.subtract(maxDays, "days") : pendingDateFrom;
        }

        form.setFieldValue("pendingDateTo", dateTo);
        form.setFieldValue("pendingDateFrom", dateFrom);
    };

    const handleFindBy = (findByValue, form) => {
        const fieldsToReset = ["longDetail", "voucher", "detail", "movementType", "amountFrom", "amountTo"];
        fieldsToReset.forEach((field) => form.setFieldValue(field, null));

        if (findByValue === "period") {
            setDates();
        }
    };

    const handleExport = (format) => {
        const pageNumber = 1;
        const isLatestSelected = document.getElementById("lastMovements").children.length > 1;

        dispatch(
            accountsActions.downloadLatestMovements({
                format,
                isLatestMovementsSelected: isLatestSelected,
                latestMovementsPageNumber,
                pendingMovementsPageNumber,
                findBy,
                ...(enableRangeDatefield
                    ? {
                          dateFrom: isLatestSelected
                              ? downloadExportValues?.lastDates[0]
                              : downloadExportValues?.pendingDates[0],
                          dateTo: isLatestSelected
                              ? downloadExportValues?.lastDates[1]
                              : downloadExportValues?.pendingDates[1],
                      }
                    : {
                          dateFrom: isLatestSelected
                              ? downloadExportValues?.lastDateFrom
                              : downloadExportValues?.pendingDateFrom,
                          dateTo: isLatestSelected
                              ? downloadExportValues?.lastDateTo
                              : downloadExportValues?.pendingDateTo,
                      }),
                selectedAccount,
                pageNumber,
                amountFrom: downloadExportValues?.amountFrom,
                amountTo: downloadExportValues?.amountTo,
                voucher: downloadExportValues?.voucher,
                detail: downloadExportValues?.detail,
            }),
        );
    };

    const downloadButton = (
        <div className="d-flex align-items-end ml-auto">
            <Dropdown
                image="images/download_bold.svg"
                listClassName="min-width-maxcontent"
                buttonClass={classNames("btn btn-outline", { "mb-3": isDesktop }, { "m-0": !isDesktop })}
                fetching={fetchingDownload}
                imageStyle="px-4 m-0"
                pullDown>
                <Button
                    onClick={() => handleExport("pdf")}
                    label="global.file.pdf"
                    className="dropdown__item-btn dropdown__item-btn-custom btn-b-none my-0"
                />
                <Button
                    onClick={() => handleExport("xls")}
                    label="global.file.xls"
                    className="dropdown__item-btn dropdown__item-btn-custom btn-b-none my-0"
                />
                {isDesktop && (
                    <Button
                        onClick={() => handleExport("csv")}
                        label="global.file.csv"
                        className="dropdown__item-btn dropdown__item-btn-custom btn-b-none my-0"
                    />
                )}
            </Dropdown>
        </div>
    );

    const isHoliday = (date) => {
        const formattedDate = moment(date).format("DD/MM/YYYY");
        return holidays.some((holiday) => holiday === formattedDate);
    };
    const findNextAvailableDate = (date) => {
        const checkDate = moment(date).add(1, "days");
        while (isHoliday(checkDate) || checkDate.day() === 0 || checkDate.day() === 6) {
            checkDate.add(1, "days");
        }
        return checkDate;
    };

    const getSelectableDates = (today) => {
        const selectableDates = [];
        if (!isHoliday(today) && today.day() !== 0 && today.day() !== 6) {
            selectableDates.push(today);
        }
        const maxDate = findNextAvailableDate(today);
        selectableDates.push(maxDate);

        return selectableDates;
    };

    const today = moment().startOf("day");
    const selectableDates = getSelectableDates(today);

    const filterDates = (date) => {
        const targetDate = moment(date).startOf("day");
        const isInvalidDate = isHoliday(targetDate) || targetDate.day() === 0 || targetDate.day() === 6;
        return (
            !isInvalidDate &&
            selectableDates.some(
                (selectableDate) =>
                    targetDate.isSame(selectableDate, "day") || targetDate.isBefore(selectableDate, "day"),
            )
        );
    };

    return (
        <Form autoComplete="off">
            <Container
                className={classNames("align-items-center account-header-detail mb-2", {
                    "backgroud-transparent": !isDesktop,
                })}
                gridClassName="form-content px-0"
                rowClassName="d-flex flex-wrap w-100 p-3 mx-0">
                <div
                    className={classNames("d-flex align-self-center account-button-filters mt-35", {
                        "w-100": !isDesktop,
                    })}>
                    <div
                        className={classNames("data-wrapper-flex align-items-end", {
                            "justify-content-center": !isDesktop,
                            "w-100": !isDesktop,
                            "gap-1": isDesktop,
                        })}>
                        <div
                            className={classNames({
                                "tablet-selector-button": !isDesktop,
                                "w-100": !isDesktop,
                            })}>
                            <Button
                                label={`${FORM_ID}.lastMovements.label`}
                                id="lastMovements"
                                className={classNames("btn-outline btn-regular chip-selector-btn", {
                                    selected: isLatestMovementsSelected,
                                    "py-2 px-0 m-0 account-movements-selector": !isDesktop,
                                    "py-25 mx-0": isDesktop,
                                })}
                                replace={{
                                    componentProps: { className: !isDesktop ? "p-0" : "px-0" },
                                }}
                                block={false}
                                image={isLatestMovementsSelected && `images/check.svg`}
                                onClick={() => handleMovementToShow(true)}
                            />
                        </div>
                        <div
                            className={classNames({
                                "tablet-selector-button": !isDesktop,
                                "w-100": !isDesktop,
                            })}>
                            <Button
                                label={`${FORM_ID}.pendingMovements.label`}
                                id="pendingMovements"
                                className={classNames("btn-outline btn-regular chip-selector-btn", {
                                    selected: !isLatestMovementsSelected,
                                    "px-0 m-0 account-movements-selector": !isDesktop,
                                    "py-25 mx-0 min-width-maxcontent": isDesktop,
                                })}
                                block={false}
                                image={!isLatestMovementsSelected && `images/check.svg`}
                                onClick={() => handleMovementToShow(false)}
                            />
                        </div>
                    </div>
                </div>
                {!isDesktop ? (
                    <div className="w-100 justify-content-end d-flex">
                        <Button
                            block
                            className="btn-link p-0 right"
                            label={showForm ? "global.hide.filter.upper" : "global.see.filter.upper"}
                            onClick={() => setShowForm(!showForm)}
                        />
                    </div>
                ) : (
                    undefined
                )}
                {isDesktop || showForm ? (
                    <>
                        {!isDesktop ? downloadButton : undefined}
                        <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                            <Field
                                component={Selector}
                                options={
                                    isLatestMovementsSelected
                                        ? getLatestMovementsOptions()
                                        : getPendingMovementsOptions()
                                }
                                idForm={FORM_ID}
                                name="findBy"
                                inLineControl
                                isRequired
                                placeholder={i18nUtils.get("accounts.movements.findBy.placeholder")}
                                className="px-3 py-2"
                                controlLabelClassName={enableRangeDatefield ? "text-transform-none" : ""}
                                onCustomChange={handleFindBy}
                            />
                        </div>
                        {findBy === "period" && !enableRangeDatefield && (
                            <>
                                {isLatestMovementsSelected ? (
                                    <>
                                        <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                                            <Field
                                                component={DateField}
                                                hidePlaceholder
                                                idForm={FORM_ID}
                                                name="lastDateFrom"
                                                selectsStart
                                                minDate={getMaxDateFrom()}
                                                maxDate={moment().startOf("day")}
                                                autocomplete="off"
                                            />
                                        </div>
                                        <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                                            <Field
                                                component={DateField}
                                                hidePlaceholder
                                                idForm={FORM_ID}
                                                name="lastDateTo"
                                                selectsEnd
                                                minDate={lastDateFrom || getMaxDateFrom()}
                                                maxDate={selectableDates[selectableDates.length - 1]}
                                                autocomplete="off"
                                                filterDate={filterDates}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                                            <Field
                                                component={DateField}
                                                hidePlaceholder
                                                handleCustomChange={handleChangeDate("pendingDateFrom")}
                                                idForm={FORM_ID}
                                                name="pendingDateFrom"
                                                selectsStart
                                                minDate={getPendingMinDate()}
                                                maxDate={getPendingMinDate().add(+3, "months")}
                                                autocomplete="off"
                                            />
                                        </div>
                                        <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                                            <Field
                                                component={DateField}
                                                hidePlaceholder
                                                idForm={FORM_ID}
                                                handleCustomChange={handleChangeDate("pendingDateTo")}
                                                name="pendingDateTo"
                                                selectsEnd
                                                minDate={pendingDateFrom || getPendingMinDate()}
                                                maxDate={getPendingMinDate().add(+3, "months")}
                                                autocomplete="off"
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        {findBy === "period" && enableRangeDatefield && (
                            <>
                                {isLatestMovementsSelected ? (
                                    <div className={isDesktop ? "pr-3 mt-1 w-320px" : "w-100"}>
                                        <Field
                                            component={RangeDatePicker}
                                            idForm={FORM_ID}
                                            name="lastDates"
                                            minDate={getMaxDateFrom().toDate()}
                                            maxDate={selectableDates[selectableDates.length - 1]}
                                            autocomplete="off"
                                            disableWeekends
                                            required
                                        />
                                    </div>
                                ) : (
                                    <div className={isDesktop ? "pr-3 mt-1 w-320px" : "w-100"}>
                                        <Field
                                            component={RangeDatePicker}
                                            idForm={FORM_ID}
                                            name="pendingDates"
                                            minDate={getPendingMinDate().toDate()}
                                            maxDate={getPendingMinDate()
                                                .add(+3, "months")
                                                .toDate()}
                                            autocomplete="off"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {findBy === "amount" && (
                            <>
                                <div className={isDesktop ? "pr-3 w-288px" : "w-100"}>
                                    <Field
                                        autocomplete="off"
                                        component={AmountField}
                                        data={{ options: currencies }}
                                        idForm={FORM_ID}
                                        name="amountFrom"
                                        clearable={false}
                                        label="transfers.amount.label"
                                        maxLength={22}
                                        disableSelect
                                        fixedDecimalScale
                                        isNumericString
                                        controlLabelClassName={enableRangeDatefield ? "text-transform-none" : ""}
                                        useCustomHandleKeyDown
                                    />
                                </div>
                                <div className={isDesktop ? "pr-3 w-288px" : "w-100"}>
                                    <Field
                                        autocomplete="off"
                                        component={AmountField}
                                        data={{ options: currencies }}
                                        idForm={FORM_ID}
                                        name="amountTo"
                                        clearable={false}
                                        label="transfers.amount.label"
                                        maxLength={22}
                                        disableSelect
                                        fixedDecimalScale
                                        isNumericString
                                        controlLabelClassName={enableRangeDatefield ? "text-transform-none" : ""}
                                        useCustomHandleKeyDown
                                    />
                                </div>
                            </>
                        )}
                        {findBy === "voucher" && isLatestMovementsSelected ? (
                            <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                                <Field
                                    component={AlfaNumericStgField}
                                    hidePlaceholder
                                    idForm={FORM_ID}
                                    name={findBy}
                                    type="text"
                                    maxLength="9"
                                    errorClassName="p-absolute"
                                    isNumeric
                                    controlLabelClassName={enableRangeDatefield ? "text-transform-none" : ""}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {findBy === "detail" || findBy === "longDetail" ? (
                            <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                                <Field
                                    component={TextField}
                                    hidePlaceholder
                                    idForm={FORM_ID}
                                    name={findBy}
                                    type="text"
                                    maxLength={findBy === "longDetail" ? "50" : "200"}
                                    errorClassName="p-absolute"
                                    controlLabelClassName={enableRangeDatefield ? "text-transform-none" : ""}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {findBy === "movementType" && !isLatestMovementsSelected && (
                            <div className={isDesktop ? "pr-3 w-224px" : "w-100"}>
                                <Field
                                    component={Selector}
                                    options={TYPE_OPTIONS}
                                    idForm={FORM_ID}
                                    name="movementType"
                                    inLineControl
                                    isRequired
                                    placeholder={i18nUtils.get("accounts.movements.findBy.placeholder")}
                                    style={{ padding: "12px 1rem" }}
                                    controlLabelClassName={enableRangeDatefield ? "text-transform-none" : ""}
                                />
                            </div>
                        )}
                        <div className={isDesktop ? "d-flex align-items-end pr-3 pl-0" : "w-100"}>
                            <Button
                                bsStyle="primary"
                                label={`${FORM_ID}.btn.filter.label`}
                                loading={isFetchingMovements}
                                type="submit"
                                className={classNames({
                                    "text-transform-none": enableNewSubmitButton,
                                    "filter-button mb-3": isDesktop,
                                })}
                                disabled={
                                    (enableNewSubmitButton &&
                                        evalEqualsValues(
                                            {
                                                ...filters,
                                                amountFrom: filters.amountFrom?.amount,
                                                amountTo: filters.amountTo?.amount,
                                            },
                                            {
                                                ...values,
                                                amountFrom: values.amountFrom?.amount,
                                                amountTo: values.amountTo?.amount,
                                            },
                                            [
                                                "dateFrom",
                                                "dateTo",
                                                ...(!(
                                                    findBy === "period" &&
                                                    enableRangeDatefield &&
                                                    isLatestMovementsSelected
                                                )
                                                    ? ["lastDates"]
                                                    : []),
                                                ...(!(
                                                    findBy === "period" &&
                                                    enableRangeDatefield &&
                                                    !isLatestMovementsSelected
                                                )
                                                    ? ["pendingDates"]
                                                    : []),
                                                ...(!(
                                                    findBy === "period" &&
                                                    !enableRangeDatefield &&
                                                    isLatestMovementsSelected
                                                )
                                                    ? ["lastDateFrom", "lastDateTo"]
                                                    : []),
                                                ...(!(
                                                    findBy === "period" &&
                                                    !enableRangeDatefield &&
                                                    !isLatestMovementsSelected
                                                )
                                                    ? ["pendingDateFrom", "pendingDateTo"]
                                                    : []),
                                                ...(findBy !== "amount" ? ["amountFrom", "amountTo"] : []),
                                                ...(findBy !== "voucher" ? ["voucher"] : []),
                                                ...(findBy !== "detail" ? ["detail"] : []),
                                                ...(findBy !== "longDetail" ? ["longDetail"] : []),
                                                ...(findBy !== "movementType" ? ["movementType"] : []),
                                            ],
                                            [
                                                ...(findBy === "period" &&
                                                enableRangeDatefield &&
                                                isLatestMovementsSelected
                                                    ? ["lastDates"]
                                                    : []),
                                                ...(findBy === "period" &&
                                                enableRangeDatefield &&
                                                !isLatestMovementsSelected
                                                    ? ["pendingDates"]
                                                    : []),
                                                ...(findBy === "period" &&
                                                !enableRangeDatefield &&
                                                isLatestMovementsSelected
                                                    ? ["lastDateFrom", "lastDateTo"]
                                                    : []),
                                                ...(findBy === "period" &&
                                                !enableRangeDatefield &&
                                                !isLatestMovementsSelected
                                                    ? ["pendingDateFrom", "pendingDateTo"]
                                                    : []),
                                            ],
                                        )) ||
                                    isButtonDisabled
                                }
                                image={enableNewSubmitButton ? "filter-icon.svg" : undefined}
                                imageStyle="mr-2"
                                onClick={() => {
                                    setDownloadValues();
                                }}
                            />
                        </div>
                        {isDesktop ? downloadButton : undefined}
                    </>
                ) : (
                    undefined
                )}
            </Container>
        </Form>
    );
}

const mapStateToProps = (state) => ({
    isFetchingMovements: accountsSelectors.getFetchingMovements(state),
    selectedAccount: accountsSelectors.getSelectedAccount(state),
    latestMovementsPageNumber: accountsSelectors.getLatestMovementsPageNumber(state),
    pendingMovementsPageNumber: accountsSelectors.getPendingMovementsPageNumber(state),
    holidays: holidaysSelector.getHolidays(state),
    filters: accountsSelectors.getFilters(state),
});

MovementsFilters.propTypes = {
    dispatch: PropTypes.func.isRequired,
    isDesktop: PropTypes.func.isRequired,
    selectedAccount: PropTypes.shape({
        currency: PropTypes.string,
    }).isRequired,
    findBy: PropTypes.string.isRequired,
    isLatestMovementsSelected: PropTypes.bool.isRequired,
    lastDateFrom: PropTypes.string.isRequired,
    lastDateTo: PropTypes.string.isRequired,
    pendingDateFrom: PropTypes.string.isRequired,
    pendingDateTo: PropTypes.string.isRequired,
    amountFrom: PropTypes.number.isRequired,
    amountTo: PropTypes.number.isRequired,
    voucher: PropTypes.string.isRequired,
    detail: PropTypes.string.isRequired,
    movementType: PropTypes.string.isRequired,
    isFetchingMovements: PropTypes.bool.isRequired,
    latestMovementsPageNumber: PropTypes.number.isRequired,
    pendingMovementsPageNumber: PropTypes.number.isRequired,
    getPendingMinDate: PropTypes.func.isRequired,
    handleMovementToShow: PropTypes.func.isRequired,
    setDates: PropTypes.func.isRequired,
    holidays: PropTypes.arrayOf(PropTypes.string).isRequired,
    fetchingDownload: PropTypes.bool.isRequired,
    downloadExportValues: PropTypes.shape({
        lastDates: PropTypes.arrayOf(PropTypes.string).isRequired,
        pendingDates: PropTypes.arrayOf(PropTypes.string).isRequired,
        lastDateFrom: PropTypes.string.isRequired,
        lastDateTo: PropTypes.string.isRequired,
        pendingDateFrom: PropTypes.string.isRequired,
        pendingDateTo: PropTypes.string.isRequired,
        amountFrom: PropTypes.number.isRequired,
        amountTo: PropTypes.number.isRequired,
        voucher: PropTypes.string.isRequired,
        detail: PropTypes.string.isRequired,
    }).isRequired,
    setDownloadValues: PropTypes.func.isRequired,
    lastDates: PropTypes.arrayOf(PropTypes.string).isRequired,
    pendingDates: PropTypes.arrayOf(PropTypes.string).isRequired,
    longDetail: PropTypes.string.isRequired,
    values: PropTypes.shape({}).isRequired,
    filters: PropTypes.shape({}).isRequired,
};
export default compose(connect(mapStateToProps))(MovementsFilters);
